import { useCallback, useEffect, useRef, useState } from "react";
import { authFetch } from "../lib/api";
import {
  AlertTriangle,
  Calendar,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Loader2,
  MapPin,
  Plus,
  RefreshCw,
  Trash2,
  Users,
} from "lucide-react";

const RSVP_LABEL = {
  going: "Going",
  maybe: "Maybe",
  not_going: "Declined",
};

const RSVP_COLOR = {
  going: "text-emerald-600 bg-emerald-50 border-emerald-200",
  maybe: "text-amber-600 bg-amber-50 border-amber-200",
  not_going: "text-rose-600 bg-rose-50 border-rose-200",
};

export const CalendarFeedsPanel = ({ facility }) => {
  const fid = facility.facility_id;
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(null);
  const [form, setForm] = useState({ display_name: "", feed_url: "" });
  const [adding, setAdding] = useState(false);
  const [msg, setMsg] = useState(null);

  const [events, setEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());
  const [rsvpsByEvent, setRsvpsByEvent] = useState({});
  const [rsvpLoading, setRsvpLoading] = useState(null);

  const flash = (text, err = false) => {
    setMsg({ text, err });
    setTimeout(() => setMsg(null), 4000);
  };

  const loadFeeds = useCallback(async () => {
    setLoading(true);
    const data = await authFetch(
      `/fieldiq/ics-feeds?facility_id=${encodeURIComponent(fid)}`,
    );
    setFeeds(Array.isArray(data) ? data : []);
    setLoading(false);
  }, [fid]);

  useEffect(() => {
    loadFeeds();
  }, [loadFeeds]);

  // Read inside loadEvents without making it depend on `expanded` — that would
  // re-run the mount effect on every expand and collapse.
  const expandedRef = useRef(expanded);
  useEffect(() => {
    expandedRef.current = expanded;
  }, [expanded]);

  const fetchRsvps = useCallback(
    async (eventId) => {
      setRsvpLoading(eventId);
      const data = await authFetch(
        `/admin/events/${eventId}/rsvps?facility_id=${encodeURIComponent(fid)}`,
      );
      setRsvpsByEvent((prev) => ({
        ...prev,
        [eventId]: Array.isArray(data) ? data : [],
      }));
      setRsvpLoading(null);
    },
    [fid],
  );

  const loadEvents = useCallback(async () => {
    setEventsLoading(true);
    const data = await authFetch(
      `/admin/events?facility_id=${encodeURIComponent(fid)}`,
    );
    setEvents(Array.isArray(data) ? data : []);
    setEventsLoading(false);
    // Attendee lists are cached per event on first expand and would otherwise
    // survive the refresh, leaving an open row showing who had responded
    // before the sync. Drop the cache and refill the rows still open.
    setRsvpsByEvent({});
    expandedRef.current.forEach((id) => fetchRsvps(id));
  }, [fid, fetchRsvps]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const toggleExpanded = async (event) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(event.id)) next.delete(event.id);
      else next.add(event.id);
      return next;
    });
    if (!rsvpsByEvent[event.id]) {
      await fetchRsvps(event.id);
    }
  };

  const fmtEventDate = (iso) =>
    iso
      ? new Date(iso).toLocaleString("en-US", {
          weekday: "short",
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        })
      : "—";

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.feed_url.trim()) return;
    setAdding(true);
    const res = await authFetch("/fieldiq/ics-feeds", {
      method: "POST",
      body: JSON.stringify({
        facility_id: fid,
        display_name: form.display_name.trim() || "Calendar",
        feed_url: form.feed_url.trim(),
      }),
    });
    setAdding(false);
    if (res && res.error) {
      flash(res.error, true);
      return;
    }
    const synced = res?.sync?.synced ?? 0;
    flash(`Feed added — synced ${synced} event${synced !== 1 ? "s" : ""}.`);
    setForm({ display_name: "", feed_url: "" });
    loadFeeds();
    loadEvents(); // the events it just synced in belong in the list below
  };

  const handleSync = async (feedId) => {
    setSyncing(feedId);
    const res = await authFetch(
      `/fieldiq/ics-feeds/${feedId}/sync?facility_id=${encodeURIComponent(fid)}`,
      {
        method: "POST",
        body: JSON.stringify({ facility_id: fid }),
      },
    );
    setSyncing(null);
    if (res && res.error) {
      flash(res.error, true);
      return;
    }
    flash(
      `Synced ${res?.synced ?? 0} event${(res?.synced ?? 0) !== 1 ? "s" : ""}.`,
    );
    loadFeeds();
    // The whole point of a sync is that start_at/end_at may have moved — the
    // upsert updates them, so the list has to be re-read or it keeps showing
    // the date and time from before.
    loadEvents();
  };

  const handleToggle = async (feed) => {
    await authFetch(`/fieldiq/ics-feeds/${feed.id}`, {
      method: "PATCH",
      body: JSON.stringify({
        facility_id: fid,
        sync_enabled: !feed.sync_enabled,
      }),
    });
    setFeeds((prev) =>
      prev.map((f) =>
        f.id === feed.id ? { ...f, sync_enabled: !f.sync_enabled } : f,
      ),
    );
  };

  const handleDelete = async (feed) => {
    if (
      !window.confirm(
        `Delete "${feed.display_name}" and all its synced events?`,
      )
    )
      return;
    const res = await authFetch(
      `/fieldiq/ics-feeds/${feed.id}?facility_id=${encodeURIComponent(fid)}`,
      { method: "DELETE" },
    );
    if (res && res.error) {
      flash(res.error, true);
      return;
    }
    flash("Feed deleted.");
    setFeeds((prev) => prev.filter((f) => f.id !== feed.id));
    // The confirm promises "and all its synced events" — so drop them from the
    // list too, rather than leaving events for a feed that no longer exists.
    loadEvents();
  };

  const fmtDate = (iso) =>
    iso
      ? new Date(iso).toLocaleString("en-US", {
          month: "short",
          day: "numeric",
          hour: "numeric",
          minute: "2-digit",
        })
      : "Never";

  return (
    <div className="max-w-2xl space-y-5">
      {msg && (
        <div
          className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm border ${msg.err ? "bg-red-50 border-red-200 text-red-700" : "bg-green-50 border-green-200 text-green-700"}`}
        >
          {msg.err ? (
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          ) : (
            <CheckCircle className="w-4 h-4 flex-shrink-0" />
          )}
          {msg.text}
        </div>
      )}

      {/* Add feed form */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
        <h3 className="text-sm font-semibold text-slate-700 mb-4 flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add Calendar Feed
        </h3>
        <form onSubmit={handleAdd} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Display Name
            </label>
            <input
              type="text"
              value={form.display_name}
              onChange={(e) =>
                setForm((f) => ({ ...f, display_name: e.target.value }))
              }
              placeholder="e.g. District Events"
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              .ics Feed URL <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              required
              value={form.feed_url}
              onChange={(e) =>
                setForm((f) => ({ ...f, feed_url: e.target.value }))
              }
              placeholder="https://calendar.google.com/calendar/ical/…/basic.ics"
              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-slate-400 mt-1">
              Google Calendar: Settings → "Secret address in iCal format"
            </p>
          </div>
          <button
            type="submit"
            disabled={adding}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {adding ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            {adding ? "Adding…" : "Add Feed"}
          </button>
        </form>
      </div>

      {/* Feed list */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm divide-y divide-slate-100">
        <div className="px-5 py-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700">
            Connected Feeds{" "}
            {feeds.length > 0 && (
              <span className="ml-1 text-xs font-normal text-slate-400">
                ({feeds.length})
              </span>
            )}
          </h3>
          <button
            onClick={loadFeeds}
            disabled={loading}
            className="p-1.5 text-slate-400 hover:text-slate-600 rounded"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
        {loading && feeds.length === 0 ? (
          <div className="px-5 py-6 text-center text-sm text-slate-400">
            Loading…
          </div>
        ) : feeds.length === 0 ? (
          <div className="px-5 py-6 text-center text-sm text-slate-400">
            No feeds connected yet.
          </div>
        ) : (
          feeds.map((feed) => (
            <div key={feed.id} className="px-5 py-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <Calendar className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">
                      {feed.display_name}
                    </p>
                    <p className="text-xs text-slate-400 truncate mt-0.5">
                      {feed.feed_url}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Last sync: {fmtDate(feed.last_synced_at)}
                    </p>
                    {feed.last_error && (
                      <p className="text-xs text-red-500 mt-1">
                        {feed.last_error}
                      </p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => handleToggle(feed)}
                    title={feed.sync_enabled ? "Pause sync" : "Enable sync"}
                    className={`text-xs px-2 py-1 rounded border ${feed.sync_enabled ? "border-green-300 text-green-700 bg-green-50" : "border-slate-200 text-slate-500"}`}
                  >
                    {feed.sync_enabled ? "Active" : "Paused"}
                  </button>
                  <button
                    onClick={() => handleSync(feed.id)}
                    disabled={syncing === feed.id}
                    title="Sync now"
                    className="p-1.5 text-slate-400 hover:text-blue-600 rounded"
                  >
                    <RefreshCw
                      className={`w-4 h-4 ${syncing === feed.id ? "animate-spin" : ""}`}
                    />
                  </button>
                  <button
                    onClick={() => handleDelete(feed)}
                    title="Delete"
                    className="p-1.5 text-slate-400 hover:text-red-500 rounded"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Upcoming events */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm divide-y divide-slate-100">
        <div className="px-5 py-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-700">
            Upcoming Events{" "}
            {events.length > 0 && (
              <span className="ml-1 text-xs font-normal text-slate-400">
                ({events.length})
              </span>
            )}
          </h3>
          <button
            onClick={loadEvents}
            disabled={eventsLoading}
            className="p-1.5 text-slate-400 hover:text-slate-600 rounded"
          >
            <RefreshCw
              className={`w-4 h-4 ${eventsLoading ? "animate-spin" : ""}`}
            />
          </button>
        </div>
        {eventsLoading && events.length === 0 ? (
          <div className="px-5 py-6 text-center text-sm text-slate-400">
            Loading…
          </div>
        ) : events.length === 0 ? (
          <div className="px-5 py-6 text-center text-sm text-slate-400">
            No upcoming events. Connect a feed above to sync some in.
          </div>
        ) : (
          events.map((ev) => {
            const isOpen = expanded.has(ev.id);
            const rsvps = rsvpsByEvent[ev.id];
            return (
              <div key={ev.id} className="px-5 py-4">
                <div className="flex items-start gap-3">
                  <Calendar className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-slate-800">
                      {ev.title}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {fmtEventDate(ev.start_at)}
                    </p>
                    {ev.location_name && (
                      <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-1">
                        <MapPin className="w-3 h-3 flex-shrink-0" />
                        {ev.location_name}
                      </p>
                    )}
                    {ev.agenda && (
                      <p className="text-xs text-slate-600 mt-2 whitespace-pre-wrap">
                        {ev.agenda}
                      </p>
                    )}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className="text-xs px-2 py-0.5 rounded border border-emerald-200 bg-emerald-50 text-emerald-700">
                        Going {ev.going_count || 0}
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-700">
                        Maybe {ev.maybe_count || 0}
                      </span>
                      <span className="text-xs px-2 py-0.5 rounded border border-rose-200 bg-rose-50 text-rose-700">
                        Declined {ev.not_going_count || 0}
                      </span>
                      <button
                        onClick={() => toggleExpanded(ev)}
                        className="ml-1 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700"
                      >
                        <Users className="w-3 h-3" />
                        {isOpen ? "Hide who responded" : "Who responded"}
                        {isOpen ? (
                          <ChevronUp className="w-3 h-3" />
                        ) : (
                          <ChevronDown className="w-3 h-3" />
                        )}
                      </button>
                    </div>
                    {isOpen && (
                      <div className="mt-3 border-t border-slate-100 pt-3">
                        {rsvpLoading === ev.id && !rsvps ? (
                          <p className="text-xs text-slate-400">Loading…</p>
                        ) : !rsvps || rsvps.length === 0 ? (
                          <p className="text-xs text-slate-400">
                            No responses yet.
                          </p>
                        ) : (
                          <ul className="space-y-1.5">
                            {rsvps.map((r) => (
                              <li
                                key={r.user_id}
                                className="flex items-center justify-between gap-3 text-xs"
                              >
                                <span className="text-slate-700 truncate">
                                  {r.name}
                                  {r.checked_in_at && (
                                    <span className="ml-1.5 text-slate-400">
                                      · checked in
                                    </span>
                                  )}
                                </span>
                                <span
                                  className={`px-2 py-0.5 rounded border flex-shrink-0 ${RSVP_COLOR[r.status] || "border-slate-200 text-slate-500"}`}
                                >
                                  {RSVP_LABEL[r.status] || r.status}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};
